import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Input,
  Card,
  Alert,
  Modal,
  ModalDialog,
  ModalClose
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [newVendor, setNewVendor] = useState({
    name: '',
    mobile: '',
    email: '',
    address: ''
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/vendors');
      setVendors(response.data.vendors || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleNewVendorChange = (e) => {
    setNewVendor((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAddVendor = async (e) => {
    e.preventDefault();
    setAddError('');
    if (!newVendor.name.trim()) {
      setAddError('Vendor name is required');
      return;
    }
    try {
      setAddLoading(true);
      await axiosInstance.post('/vendors', {
        name: newVendor.name.trim(),
        mobile: newVendor.mobile.trim() || undefined,
        email: newVendor.email.trim() || undefined,
        address: newVendor.address.trim() || undefined
      });
      setNewVendor({ name: '', mobile: '', email: '', address: '' });
      setAddModalOpen(false);
      fetchVendors();
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to add vendor');
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography level="h1" sx={{ color: '#eb7a24' }}>
          Vendors
        </Typography>
        <Button
          onClick={() => setAddModalOpen(true)}
          sx={{
            backgroundColor: '#eb7a24',
            '&:hover': { backgroundColor: '#cc6a1f' }
          }}
        >
          Add Vendor
        </Button>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <Box
          sx={{
            overflowX: 'auto',
            '& thead th': {
              backgroundColor: '#f5f5f5',
              padding: '12px 16px',
              textAlign: 'left',
              fontWeight: 'bold',
              borderBottom: '2px solid #eb7a24'
            },
            '& tbody td': {
              padding: '12px 16px',
              borderBottom: '1px solid #eee'
            },
            '& tbody tr:nth-of-type(even)': {
              backgroundColor: '#f9f9f9'
            },
            '& tbody tr:hover': {
              backgroundColor: '#fff4e6'
            },
            '& td': {
              wordWrap: 'break-word',
              whiteSpace: 'normal'
            }
          }}
        >
          <Table>
            <thead>
              <tr>
                <th style={{ width: '20%' }}>Name</th>
                <th style={{ width: '15%' }}>Mobile</th>
                <th style={{ width: '20%' }}>Email</th>
                <th style={{ width: '45%' }}>Address</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                    Loading...
                  </td>
                </tr>
              ) : vendors.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                    No vendors yet. Add one to use when adding materials or disposables.
                  </td>
                </tr>
              ) : (
                vendors.map((v) => (
                  <tr key={v.id}>
                    <td style={{ color: '#eb7a24', fontWeight: 'bold' }}>{v.name}</td>
                    <td>{v.mobile || '—'}</td>
                    <td>{v.email || '—'}</td>
                    <td>{v.address || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Box>
      </Card>

      <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 400 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2, color: '#eb7a24' }}>
            Add Vendor
          </Typography>
          {addError && (
            <Alert color="danger" sx={{ mb: 2 }} onClose={() => setAddError('')}>
              {addError}
            </Alert>
          )}
          <form onSubmit={handleAddVendor}>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Name *</FormLabel>
              <Input
                name="name"
                value={newVendor.name}
                onChange={handleNewVendorChange}
                required
                placeholder="Vendor name"
                sx={{
                  '&:hover': { borderColor: '#ea7a23' },
                  '&:focus-within': { borderColor: '#ea7a23' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Mobile</FormLabel>
              <Input
                name="mobile"
                value={newVendor.mobile}
                onChange={handleNewVendorChange}
                placeholder="Mobile number"
                sx={{
                  '&:hover': { borderColor: '#ea7a23' },
                  '&:focus-within': { borderColor: '#ea7a23' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={newVendor.email}
                onChange={handleNewVendorChange}
                placeholder="Email"
                sx={{
                  '&:hover': { borderColor: '#ea7a23' },
                  '&:focus-within': { borderColor: '#ea7a23' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Address</FormLabel>
              <Input
                name="address"
                value={newVendor.address}
                onChange={handleNewVendorChange}
                placeholder="Address"
                sx={{
                  '&:hover': { borderColor: '#ea7a23' },
                  '&:focus-within': { borderColor: '#ea7a23' }
                }}
              />
            </FormControl>
            <Button
              type="submit"
              loading={addLoading}
              fullWidth
              sx={{
                backgroundColor: '#eb7a24',
                '&:hover': { backgroundColor: '#cc6a1f' }
              }}
            >
              Add Vendor
            </Button>
          </form>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default Vendors;
